import { CommentsInstagram, defaultImage, InfinityScroll, Loader, LogService, Media, Selector, VisionService } from '@hub/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import './photo-comments-instagram.scoped.scss'
import './photo-comments-instagram.scss'

interface PhotoCommentsInstagramProps extends React.HTMLAttributes<HTMLDivElement> {
  photo: Media | undefined
  comments?: Array<CommentsInstagram>
  minComments?: number
  idProfile?: string
  onChangeComments?: (comments: Array<CommentsInstagram>) => any
  selectMode?: boolean
}

export default ({
  photo,
  minComments = 3,
  comments = [],
  idProfile,
  onChangeComments,
  selectMode = false,
  className = '',
}: PhotoCommentsInstagramProps) => {
  const [t] = useTranslation('photoComments')
  const [selectComments, setSelectComments] = useState<Array<CommentsInstagram>>(comments)
  const [localPhoto, setLocalPhoto] = useState(photo)
  const [commentsList, setCommentsList] = useState<Array<CommentsInstagram>>([])
  const [loading, setLoading] = useState(false)
  const [loadingPagination, setLoadingPagination] = useState(false)
  const [nextUrl, setNextUrl] = useState('')
  const [error, setError] = useState(false)
  const visionService = useMemo(() => new VisionService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('Photo Comments Instagram')

  useEffect(() => {
    if (selectMode && idProfile && localPhoto?.metadados?.idFacebook && !loading) {
      setLoading(true)
      visionService
        .getCommentsFromMedia(idProfile, 'instagram', localPhoto?.metadados?.idFacebook, false, true)
        .then((response) => {
          setError(false)
          setCommentsList(response?.comments)
          setNextUrl(response?.pagination?.next_url || '')
          if (selectComments?.some((sc) => sc.id !== response?.comments?.find((c: CommentsInstagram) => c.id === sc.id)?.id)) {
            setSelectComments([])
            if (onChangeComments && typeof onChangeComments === 'function') {
              onChangeComments([])
            }
          }
        })
        .catch((error) => {
          setError(true)
          logService.error({ message: 'Failed to get comments media', error })
        })
        .finally(() => {
          setLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectMode, idProfile, localPhoto])

  useEffect(() => {
    if (localPhoto?.uid !== photo?.uid) {
      setLocalPhoto(photo)
      setSelectComments([])
    }
  }, [localPhoto?.uid, photo])

  const nextPage = useCallback(() => {
    if (nextUrl && !loadingPagination) {
      setLoadingPagination(true)
      visionService
        .getCommentsFromMedia(idProfile!, 'instagram', localPhoto?.metadados?.idFacebook || '', false, true, nextUrl)
        .then((response) => {
          setError(false)
          setCommentsList([...commentsList, ...response?.comments])
          setNextUrl(response?.pagination?.next_url || '')
        })
        .catch((error) => {
          setError(true)
          logService.error({ message: 'Failed to get comments media', error })
        })
        .finally(() => {
          setLoadingPagination(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextUrl])

  const isDisabled = useCallback(
    (c: CommentsInstagram) => {
      return selectComments?.length >= minComments && !selectComments.find((element) => element.id === c.id)
    },
    [selectComments, minComments]
  )

  const handleChangeComments = useCallback(
    (comment: CommentsInstagram, checked: boolean) => {
      let newComments: Array<any> = [...selectComments]
      if (checked) {
        newComments = [...selectComments, comment]
      } else {
        newComments = selectComments.filter((item) => item.id !== comment.id)
      }
      setSelectComments(newComments)
      if (onChangeComments && typeof onChangeComments === 'function') {
        onChangeComments(newComments)
      }
    },
    [selectComments, onChangeComments]
  )

  let commentsHtml = (c: CommentsInstagram) => (
    <div className={`comment ${selectMode ? 'select-mode' : ''}`}>
      <span style={{ fontSize: '0.9rem' }}>
        <strong className="gray mr-2">{`@${c?.from?.username}`}</strong>
        {c?.text}
      </span>
      <span className="icon-comment">
        <i className="m-0 red fa-solid fa-heart"></i>
      </span>
    </div>
  )
  if (selectMode) {
    commentsHtml = useCallback(
      (c: CommentsInstagram) => (
        <Selector
          leftLabel={
            <span className={`${isDisabled(c) ? 'is-disabled' : ''}`} style={{ fontSize: '0.9rem' }}>
              <strong className="gray mr-2">{`@${c?.from?.username}`}</strong>
              {c?.text}
            </span>
          }
          errorSpan={false}
          className="checkbox-comments-selector"
          name={`checkbox-${c?.id}`}
          id={`checkbox-${c?.id}`}
          checked={!!selectComments.find((element) => element.id === c.id)}
          onChange={(checked: boolean) => handleChangeComments(c, checked)}
          disabled={isDisabled(c)}
        />
      ),
      [selectComments, handleChangeComments, isDisabled]
    )
  }

  let listContent = comments?.length ? (
    <ul className="comments-favorite">
      {comments?.map((comment, i) => (
        <li key={i}>{commentsHtml(comment)}</li>
      ))}
    </ul>
  ) : (
    <p className="error gray mt-3 text-center" style={{ fontSize: '0.9rem' }}>
      {t('emptyComments')}
    </p>
  )
  if (selectMode) {
    listContent = commentsList?.length ? (
      <ul id="comments-list-to-select-media-kit" className={`comments-favorite scrollbar ${selectMode ? 'select-mode' : ''}`}>
        <InfinityScroll
          loading={loadingPagination}
          className="wrapper-comments-modal-photo"
          elementToScrollId="comments-list-to-select-media-kit"
          endMessage={' '}
          hasMore={!!nextUrl}
          onLoadMore={nextPage}
        >
          {commentsList?.map((comment, i) => (
            <li className={`comment ${selectMode ? 'select-mode' : ''}`} key={i}>
              {commentsHtml(comment)}
            </li>
          ))}
        </InfinityScroll>
      </ul>
    ) : (
      <p className="error gray mt-3 text-center" style={{ fontSize: '0.9rem' }}>
        {t('emptyComments')}
      </p>
    )
    if (error) {
      listContent = (
        <p className="error gray mt-3 text-center" style={{ fontSize: '0.9rem' }}>
          {t('errorComments')}
        </p>
      )
    }
    if (loading) {
      listContent = (
        <div className="wrapper-loader mt-3 text-center">
          <Loader size="small" />
        </div>
      )
    }
  }

  return (
    <div className={`box p-3 photo-comment ${className}`}>
      {!selectMode && localPhoto ? (
        <span className="change-icon">
          <i className="fa-regular fa-arrows-repeat"></i>
        </span>
      ) : null}
      <img
        src={localPhoto?.imagens?.thumbnail?.url}
        className="img-fluid img-selected"
        title={t('titleImg')}
        alt={t('titleImg')}
        onError={({ currentTarget }) => {
          currentTarget.onerror = null
          currentTarget.src = defaultImage
        }}
      />
      <footer>{listContent}</footer>
    </div>
  )
}
